import { createSelector } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { mapChampionshipSettings } from 'modules/championship/domain/data-mappers/championship-settings.data-mapper';
import { getPeriodLabel } from 'shared/utils/period-label';
import { ShootoutAttempt } from 'shared/types/shootout';
import { countShootoutAttempts } from 'shared/utils/shootout';
import { RootState } from 'store/store';

const selectGameInfo = (state: RootState) => state.overlayV2.gameInfo;
const selectPeriod = (state: RootState) => state.overlayV2.time.period;

export const selectGoals = createSelector([selectGameInfo], (gameInfo) => gameInfo.goals);

export const selectShootoutAttempts = createSelector([selectGameInfo], (gameInfo) => gameInfo.shootout.attempts);

/** Счёт серии — производная от последовательности ударов, отдельно он нигде не хранится. */
const selectShootoutCount = (outcome: ShootoutAttempt) =>
  createSelector([selectShootoutAttempts], (attempts) => ({
    first: countShootoutAttempts(attempts.first, outcome),
    second: countShootoutAttempts(attempts.second, outcome),
  }));

export const selectShootoutGoals = selectShootoutCount('GOAL');

export const selectShootoutMisses = selectShootoutCount('MISS');

export const selectOrganizer = createSelector([selectGameInfo], (gameInfo) => gameInfo.organizer);

export const selectChampionship = createSelector([selectGameInfo], (gameInfo) => gameInfo.championship);

export const selectSportType = createSelector([selectGameInfo], (gameInfo) => gameInfo.sportType);

/** Регламент соревнования: он размечает, где основные таймы, где дополнительные, где серия. */
export const selectChampionshipSettings = createSelector([selectChampionship], (championship) =>
  mapChampionshipSettings(championship?.settings),
);

/** Подпись тайма для табло: «2», «ДВ-1», «Пен.» — по виду спорта и регламенту. */
export const selectPeriodLabel = createSelector(
  [selectPeriod, selectChampionshipSettings, selectSportType],
  (period, settings, sportType) => getPeriodLabel({ period, settings, sportType }),
);

export const selectMatch = createSelector([selectGameInfo], (gameInfo) => gameInfo.match);

export const selectTeams = createSelector([selectGameInfo], (gameInfo) => gameInfo.teams);

export const selectLoading = createSelector([selectGameInfo], (gameInfo) => ({
  isLoading: gameInfo.isLoading,
  isLoaded: gameInfo.isLoaded,
}));

export const selectSynchronization = createSelector([selectGameInfo], (gameInfo) => ({
  isSynchronization: gameInfo.isSynchronization,
  isSynchronize: gameInfo.isSynchronize,
}));

export const selectGameInfoError = createSelector([selectGameInfo], (gameInfo) => gameInfo.error);

export const selectFormatStartDate = createSelector([selectMatch], (match) => {
  const date = match?.date ? new Date(match?.date) : undefined;

  return {
    date: date ? format(date, 'd MMMM', { locale: ru }) : 'Неизвестная дата',
    time: date ? format(date, 'HH:mm') : 'Неизвестное время',
  };
});

export const selectFoulsByCurrentPeriod = createSelector(
  [selectGameInfo, selectPeriod],
  (gameInfo, period) => gameInfo.fouls[period] ?? { first: 0, second: 0 },
);
